/**
 * Модуль для создания и настройки экземпляров Axios с поддержкой аутентификации и загрузки изображений.
 * Использует перехватчики запросов/ответов для автоматического обновления токенов доступа.
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { getTokens, storeTokens } from "../../utils/storeTokens";
import { UserApi } from "../api/user.api";

/**
 * Текущий индекс домена для API-запросов
 */
const currentDomain = 1;

/**
 * Список доступных доменов API
 */
const domains = [
  "https://dev.advafert.ru",
  "https://api.mobile.expresstest.ru",
  "http://95.53.245.74:8000",
  "http://dev.advafert.ru:5050",
  "http://192.168.88.248:8080",
  "http://127.0.0.1:8000/",
];

/**
 * Активный домен API
 */
export const domain = domains[currentDomain];
const URL = domain + "/api/";

/**
 * Базовый экземпляр Axios для общих запросов
 * @remarks Настроен с базовым URL, JSON-заголовками и cookies
 */
const api = axios.create({
  baseURL: URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/**
 * Экземпляр Axios для аутентифицированных запросов
 * @remarks Автоматически добавляет Bearer-токен и обрабатывает истечение срока действия токена
 */
const authApi = axios.create({
  baseURL: URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/**
 * Экземпляр Axios для загрузки изображений
 * @remarks Настроен с multipart/form-data заголовками и автоматической авторизацией
 */
const imgApi = axios.create({
  baseURL: URL,
  withCredentials: true,
  headers: {
    "Content-Type": "multipart/form-data",
    Accept: "application/json",
  },
});

/**
 * Перехватчик ответов для authApi
 * Обрабатывает ошибки 401 и автоматически обновляет access token
 * @param error - Ошибка Axios
 * @returns Обновленный запрос или отклоненную промис
 */
authApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Проверка на ошибку 401 и первый цикл попытки
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const tokens = getTokens();

      try {
        // Обновление токена через API
        const tokensRes = await UserApi.RefreshToken({
          refresh: tokens.refresh,
        });

        // Сохранение новых токенов
        storeTokens({
          access: tokensRes.data.access,
          refresh: tokens.refresh,
        });

        // Добавление нового токена к запросу
        originalRequest.headers["Authorization"] =
          `Bearer ${tokensRes.data.access}`;
        return authApi(originalRequest);
      } catch (refreshError) {
        // Ошибка обновления токена - требуется повторная авторизация
        console.error("Ошибка обновления токена:", refreshError);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Перехватчик запросов для добавления Bearer-токена
 * @param config - Конфигурация запроса
 * @returns Обновленная конфигурация с авторизацией
 */
authApi.interceptors.request.use(
  async (config) => {
    const tokens = getTokens();
    const accessToken = tokens?.access;

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Перехватчик запросов для imgApi
 * Добавляет Bearer-токен к заголовкам
 * @param config - Конфигурация запроса
 * @returns Обновленная конфигурация с авторизацией
 */
imgApi.interceptors.request.use(
  async (config) => {
    const tokens = getTokens();
    const accessToken = tokens?.access;

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Экспортируемые экземпляры Axios
 */
export { api, authApi, imgApi };
